import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { getApi } from "../../api/api";
import { CategoryInterface } from "../../common/types/interfaces";
import { useScrollPagination } from "../../common/hooks/useScrollPagination";
import { useCityId } from "../../common/hooks/useCityId";

const PAGE_LIMIT = 12;

export const useCategory = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  const cityId = useCityId()

  const [allCategories, setAllCategories] = useState<CategoryInterface[]>([]);
  const [items, setItems] = useState<CategoryInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);

    getApi()
      .getCategories({ cityId })
      .then((res) => {
        if (cancelled) return;
        let data = res.data;
        if (search.trim()) {
          const lower = search.toLowerCase();
          data = data.filter(
            (c) =>
              c.name?.toLowerCase().includes(lower) ||
              c.description?.toLowerCase().includes(lower),
          );
        }
        setAllCategories(data);
        setItems(data.slice(0, PAGE_LIMIT));
        setHasMore(data.length > PAGE_LIMIT);
      })
      .catch(() => {
        if (!cancelled) {
          setAllCategories([]);
          setItems([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setIsFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [search, cityId]);

  const loadMore = useCallback(() => {
    if (isFetching || isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);

    setTimeout(() => {
      setItems((prev) => {
        const nextItems = allCategories.slice(0, prev.length + PAGE_LIMIT);
        setHasMore(nextItems.length < allCategories.length);
        return nextItems;
      });
      setIsFetchingMore(false);
    }, 300);
  }, [allCategories, hasMore, isFetching, isFetchingMore]);

  const { lastItemRef } = useScrollPagination(
    hasMore,
    isFetchingMore,
    loadMore,
  );

  return {
    items,
    total: allCategories.length,
    ready: !loading,
    isFetching,
    isFetchingMore,
    hasMore,
    loadMore,
    lastItemRef,
  };
};
